// worker.ts — typed main-thread client for the plain-JS tracking worker
// (ARCHITECTURE.md §9), which hosts the WASM tracker/SLAM/seeder: the Rust
// build at /tracker/worker.js (server-rs/crates/openscan-wasm, shipped in the
// SPA's dist/). It owns the message protocol and backpressure: at most one frame is in flight, and
// processFrame refuses a second one until the previous result is back, so the
// tracker always works on the newest frame instead of a growing queue.
// Worker `error` replies are routed by their `request` tag (§9): a failed
// frame rejects only that frame, a failed keyframeMeta only that index's
// waiters, a failed seed only the oldest seed waiter; only init/runtime
// failures (and terminate) fail everything.
// simulateSlowMs (?simulateSlowMs=N, §15.5) holds every frame result back by
// N ms while the frame still counts as in flight, so a fast machine behaves
// like a slow phone end to end (backpressure, fps, the offload switch).

import type { Detection, Intrinsics, KeyframeMeta, MapPointJSON, MarkerMap, Pose } from '../api';

export type TrackingState = 'initializing' | 'tracking' | 'lost';
export type PoseSource = 'markers' | 'features' | 'fused' | 'imu' | '';

export interface FrameResultJSON {
  state: TrackingState;
  pose_source: PoseSource;
  markers: Detection[] | null;
  have_pose: boolean;
  pose: Pose;
  intrinsics: Intrinsics;
  reproj_err_px: number;
  keyframe: boolean;
  keyframe_index: number;
  quality: number;
  tracked_points: number;
  inliers: number;
  marker_map: MarkerMap | null;
  map_points?: MapPointJSON[];
  // §14: the session dropped the lens-profile prior (estimate stayed > 3σ away).
  profile_mismatch?: boolean;
}

export interface SessionConfig {
  width: number;
  height: number;
  hfov_deg?: number;
  keyframe_min_angle_deg?: number;
  keyframe_min_translation_m?: number;
  use_imu?: boolean;
  [k: string]: unknown;
}

export interface ImuSampleJSON {
  t: number; // ms, performance.now() clock
  g: [number, number, number]; // rad/s, device axes
  a: [number, number, number]; // m/s² incl. gravity, device axes
}

export class WorkerBusyError extends Error {
  constructor() {
    super('a frame is already in flight');
    this.name = 'WorkerBusyError';
  }
}

// The tracking worker: the Rust module beside the SPA.
export const RUST_WORKER_URL = '/tracker/worker.js';

type Pending<T> = { resolve: (v: T) => void; reject: (e: Error) => void };

// parseMaybeJSON accepts either a JSON string (as the tracker returns) or an
// already-parsed object (if worker.js parses before posting).
export function parseMaybeJSON<T>(v: unknown): T {
  return (typeof v === 'string' ? JSON.parse(v) : v) as T;
}

export class ScanWorker {
  private worker: Worker;
  private ready: Promise<void>;
  private frameSeq = 0;
  private inFlight: { id: number; p: Pending<FrameResultJSON>; t0: number } | null = null;
  private seedQueue: Pending<ArrayBuffer>[] = [];
  private metaWaiters = new Map<number, Pending<KeyframeMeta>[]>();
  private failure: Error | null = null;
  private rejectReady: (e: Error) => void = () => {};
  // onError reports worker errors that no pending request absorbed (session
  // config errors, stray replies, fatal failures). Errors that reject a
  // caller's promise reach that caller instead, so they are not reported twice.
  onError: ((message: string) => void) | null = null;

  constructor(url = RUST_WORKER_URL, private simulateSlowMs = 0) {
    console.info(`[openscan/worker] starting ${url}${simulateSlowMs > 0 ? ` (simulating a slow device: +${simulateSlowMs} ms per frame)` : ''}`);
    this.worker = new Worker(url);
    let resolveReady: () => void = () => {};
    this.ready = new Promise<void>((res, rej) => {
      resolveReady = res;
      this.rejectReady = rej;
    });
    // A readiness rejection nobody awaits (e.g. terminate before whenReady)
    // must not surface as an unhandled rejection.
    this.ready.catch(() => {});
    this.worker.onmessage = (ev: MessageEvent) => this.handle(ev.data, resolveReady);
    this.worker.onerror = (ev: ErrorEvent) => {
      const err = new Error(`worker failed: ${ev.message || 'script error'}`);
      console.error('[openscan/worker]', err.message);
      this.fail(err);
    };
    this.worker.postMessage({ type: 'init' });
  }

  // whenReady resolves once the WASM module is instantiated.
  whenReady(timeoutMs = 30000): Promise<void> {
    return Promise.race([
      this.ready,
      new Promise<void>((_, rej) => globalThis.setTimeout(() => rej(new Error('WASM worker did not become ready in time')), timeoutMs)),
    ]);
  }

  get busy(): boolean {
    return this.inFlight !== null;
  }

  private handle(msg: { type?: string } & Record<string, unknown>, onReady: () => void): void {
    switch (msg.type) {
      case 'ready':
        console.info('[openscan/worker] ready');
        onReady();
        break;
      case 'frameResult': {
        const f = this.inFlight;
        if (!f || f.id !== msg.id) {
          console.warn('[openscan/worker] stray frameResult', msg.id);
          return;
        }
        if (this.simulateSlowMs > 0) {
          // Keep the frame in flight for the simulated extra time.
          globalThis.setTimeout(() => {
            if (this.inFlight !== f) return; // failed/terminated meanwhile
            this.inFlight = null;
            this.resolveFrame(f.p, msg.result);
          }, this.simulateSlowMs);
          return;
        }
        this.inFlight = null;
        this.resolveFrame(f.p, msg.result);
        break;
      }
      case 'seed': {
        const p = this.seedQueue.shift();
        if (p) p.resolve(msg.ospl as ArrayBuffer);
        break;
      }
      case 'keyframeMeta': {
        const idx = Number(msg.index);
        const waiters = this.metaWaiters.get(idx);
        this.metaWaiters.delete(idx);
        try {
          const meta = parseMaybeJSON<KeyframeMeta>(msg.meta);
          waiters?.forEach((w) => w.resolve(meta));
        } catch (err) {
          waiters?.forEach((w) => w.reject(err instanceof Error ? err : new Error(String(err))));
        }
        break;
      }
      case 'error':
        this.routeError(msg);
        break;
      default:
        console.debug('[openscan/worker] unknown message', msg.type);
    }
  }

  private resolveFrame(p: Pending<FrameResultJSON>, result: unknown): void {
    try {
      p.resolve(parseMaybeJSON<FrameResultJSON>(result));
    } catch (err) {
      p.reject(err instanceof Error ? err : new Error(String(err)));
    }
  }

  // routeError delivers a worker `error` to the request it answers (§9).
  private routeError(msg: Record<string, unknown>): void {
    const err = new Error(String(msg.message ?? 'worker error'));
    const request = typeof msg.request === 'string' ? msg.request : '';
    console.error(`[openscan/worker] error (request=${request || 'none'}):`, err.message);
    switch (request) {
      case 'frame': {
        const f = this.inFlight;
        if (f && f.id === Number(msg.id)) {
          this.inFlight = null;
          f.p.reject(err);
          return;
        }
        console.warn(`[openscan/worker] frame error for ${String(msg.id)} does not match the in-flight frame ${f?.id ?? 'none'}`);
        this.onError?.(err.message);
        return;
      }
      case 'keyframeMeta': {
        const idx = Number(msg.index);
        const waiters = this.metaWaiters.get(idx);
        this.metaWaiters.delete(idx);
        if (waiters?.length) {
          waiters.forEach((w) => w.reject(err));
          return;
        }
        console.warn(`[openscan/worker] keyframeMeta error for ${idx} with no waiter`);
        this.onError?.(err.message);
        return;
      }
      case 'seed': {
        const p = this.seedQueue.shift();
        if (p) {
          p.reject(err);
          return;
        }
        console.warn('[openscan/worker] seed error with no waiter');
        this.onError?.(err.message);
        return;
      }
      case '':
      case 'init':
        // Load failure or the Go runtime exiting: nothing will answer again.
        this.onError?.(err.message);
        this.fail(err);
        return;
      default:
        // 'session', 'free', … — no promise waits on these.
        this.onError?.(err.message);
    }
  }

  // fail rejects every outstanding request and readiness, and makes later
  // calls fail fast. Reserved for fatal failures: worker.onerror, init/runtime
  // errors and terminate.
  private fail(err: Error): void {
    this.failure = err;
    this.rejectReady(err);
    if (this.inFlight) {
      this.inFlight.p.reject(err);
      this.inFlight = null;
    }
    this.seedQueue.splice(0).forEach((p) => p.reject(err));
    this.metaWaiters.forEach((ws) => ws.forEach((w) => w.reject(err)));
    this.metaWaiters.clear();
  }

  newSession(config: SessionConfig): void {
    console.info('[openscan/worker] new session', config);
    this.worker.postMessage({ type: 'session', config });
  }

  // processFrame transfers the RGBA buffer to the worker. tMs (performance.now
  // clock) lets the IMU filter's prior apply (processFrameAt on the Go side).
  processFrame(img: ImageData, tMs: number): Promise<FrameResultJSON> {
    if (this.failure) return Promise.reject(this.failure);
    if (this.inFlight) return Promise.reject(new WorkerBusyError());
    const id = ++this.frameSeq;
    const rgba = img.data.buffer;
    return new Promise<FrameResultJSON>((resolve, reject) => {
      this.inFlight = { id, p: { resolve, reject }, t0: performance.now() };
      this.worker.postMessage({ type: 'frame', id, rgba, width: img.width, height: img.height, t: tMs }, [rgba]);
    });
  }

  seed(): Promise<ArrayBuffer> {
    if (this.failure) return Promise.reject(this.failure);
    return new Promise<ArrayBuffer>((resolve, reject) => {
      this.seedQueue.push({ resolve, reject });
      this.worker.postMessage({ type: 'seed' });
    });
  }

  keyframeMeta(index: number): Promise<KeyframeMeta> {
    if (this.failure) return Promise.reject(this.failure);
    return new Promise<KeyframeMeta>((resolve, reject) => {
      const list = this.metaWaiters.get(index) ?? [];
      list.push({ resolve, reject });
      this.metaWaiters.set(index, list);
      this.worker.postMessage({ type: 'keyframeMeta', index });
    });
  }

  pushIMU(samples: ImuSampleJSON[], screenAngle: number): void {
    if (this.failure || samples.length === 0) return;
    this.worker.postMessage({ type: 'imu', samples, screenAngle });
  }

  free(): void {
    this.worker.postMessage({ type: 'free' });
  }

  terminate(): void {
    this.fail(new Error('worker terminated'));
    this.worker.terminate();
    console.info('[openscan/worker] terminated');
  }
}
